import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { queriesApi, ctosApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Map,
  MapPin,
  Users,
  History,
  Settings,
  LogOut,
  Wifi,
  List,
  Menu,
  X as CloseIcon,
  Filter,
  Check,
  X,
  TrendingUp,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// CTO Icon
const ctoIcon = L.divIcon({
  className: "custom-cto-icon",
  html: `<div style="
    width: 24px;
    height: 24px;
    background: #FF4500;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(255,69,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
      <circle cx="12" cy="12" r="4"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function MapaDemandaPage() {
  const { user, logout } = useAuth();
  const location = useLocation();
  
  const [queries, setQueries] = useState([]);
  const [ctos, setCtos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filter, setFilter] = useState("all"); // all, viavel, inviavel, chatbot
  const [showCtos, setShowCtos] = useState(true);
  const [stats, setStats] = useState({ total: 0, viavel: 0, inviavel: 0, chatbot: 0 });

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [queriesRes, ctosRes] = await Promise.all([
          queriesApi.getAll({ limit: 1000 }),
          ctosApi.getAll()
        ]);
        
        setQueries(queriesRes.data);
        setCtos(ctosRes.data);
        
        // Calculate stats
        const total = queriesRes.data.length;
        const viavel = queriesRes.data.filter(q => q.is_viable).length;
        const inviavel = queriesRes.data.filter(q => !q.is_viable).length;
        const chatbot = queriesRes.data.filter(q => q.user_id === "chatbot").length;
        
        setStats({ total, viavel, inviavel, chatbot });
      } catch (error) {
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Filter queries
  const filteredQueries = queries.filter(q => {
    if (filter === "viavel") return q.is_viable;
    if (filter === "inviavel") return !q.is_viable;
    if (filter === "chatbot") return q.user_id === "chatbot";
    return true;
  });

  // Group by bairro for stats
  const bairroStats = {};
  queries.filter(q => !q.is_viable).forEach(q => {
    const bairro = q.bairro || "Não identificado";
    if (!bairroStats[bairro]) {
      bairroStats[bairro] = 0;
    }
    bairroStats[bairro]++;
  });
  
  const topBairros = Object.entries(bairroStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Map center
  const mapCenter = ctos.length > 0
    ? [ctos[0].latitude, ctos[0].longitude]
    : queries.length > 0
    ? [queries[0].latitude, queries[0].longitude]
    : [-22.75, -47.33];

  // Sidebar
  const Sidebar = () => (
    <aside className={`
      fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 flex flex-col
      transform transition-transform duration-300 ease-in-out
      ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      <div className="p-6 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FF4500] rounded-lg flex items-center justify-center">
            <Wifi className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
            <p className="text-xs text-slate-400">NOC Admin</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive
                  ? "bg-[#FF4500] text-white"
                  : "text-slate-400 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="mapa-demanda-page">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF4500] rounded-lg flex items-center justify-center">
              <Wifi className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold text-white">TECPLUS</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white"
          >
            {sidebarOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar />

      {/* Main Content */}
      <main className="flex-1 flex flex-col pt-16 lg:pt-0">
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-slate-800 bg-slate-900">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-white">Mapa de Demanda</h2>
              <p className="text-slate-400 text-sm">Visualize todas as consultas de viabilidade</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all" className="text-white">Todas ({stats.total})</SelectItem>
                  <SelectItem value="viavel" className="text-white">Viáveis ({stats.viavel})</SelectItem>
                  <SelectItem value="inviavel" className="text-white">Inviáveis ({stats.inviavel})</SelectItem>
                  <SelectItem value="chatbot" className="text-white">Chatbot ({stats.chatbot})</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant={showCtos ? "default" : "outline"}
                onClick={() => setShowCtos(!showCtos)}
                className={showCtos ? "bg-[#FF4500] hover:bg-[#CC3700]" : "border-slate-700 text-slate-300"}
              >
                CTOs {showCtos ? "ON" : "OFF"}
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col lg:flex-row">
          {/* Map */}
          <div className="flex-1 relative">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                <div className="spinner" />
              </div>
            ) : (
              <MapContainer
                center={mapCenter}
                zoom={12}
                className="h-[400px] lg:h-full w-full"
                zoomControl={true}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* CTOs */}
                {showCtos && ctos.map((cto) => (
                  <Marker
                    key={cto.id}
                    position={[cto.latitude, cto.longitude]}
                    icon={ctoIcon}
                  >
                    <Popup>
                      <div className="p-1">
                        <p className="font-bold text-[#FF4500]">{cto.name}</p>
                        <p className="text-xs text-slate-600">CTO Ativa</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                
                {/* Queries */}
                {filteredQueries.map((query) => (
                  <CircleMarker
                    key={query.id}
                    center={[query.latitude, query.longitude]}
                    radius={8}
                    fillColor={query.is_viable ? "#00C853" : "#D50000"}
                    color={query.is_viable ? "#00C853" : "#D50000"}
                    weight={2}
                    opacity={0.8}
                    fillOpacity={0.6}
                  >
                    <Popup>
                      <div className="p-1 min-w-[200px]">
                        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold mb-2 ${
                          query.is_viable ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {query.is_viable ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {query.is_viable ? "VIÁVEL" : "INVIÁVEL"}
                        </div>
                        <p className="text-sm font-medium text-slate-800 line-clamp-2">{query.address}</p>
                        {query.bairro && <p className="text-xs text-slate-600">Bairro: {query.bairro}</p>}
                        <p className="text-xs text-slate-500">Distância: {query.distance_meters?.toFixed(0)}m</p>
                        <p className="text-xs text-slate-500">CTO: {query.nearest_cto_name}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {query.user_name} • {new Date(query.created_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            )}
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/95 rounded-lg p-3 text-white text-sm">
              <p className="font-bold mb-2">Legenda</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <span>Viável</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500" />
                  <span>Inviável</span>
                </div>
                {showCtos && (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#FF4500]" />
                    <span>CTO</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Panel */}
          <div className="w-full lg:w-80 p-4 bg-slate-900 border-t lg:border-t-0 lg:border-l border-slate-800 overflow-auto">
            <h3 className="text-lg font-bold text-white mb-4">Estatísticas</h3>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-3">
                  <p className="text-xs text-slate-400">Total</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-3">
                  <p className="text-xs text-slate-400">Chatbot</p>
                  <p className="text-2xl font-bold text-blue-400">{stats.chatbot}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-500/10 border-green-500/30">
                <CardContent className="p-3">
                  <p className="text-xs text-green-400">Viáveis</p>
                  <p className="text-2xl font-bold text-green-400">{stats.viavel}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-500/10 border-red-500/30">
                <CardContent className="p-3">
                  <p className="text-xs text-red-400">Inviáveis</p>
                  <p className="text-2xl font-bold text-red-400">{stats.inviavel}</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Top Bairros Inviáveis */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-amber-500" />
                  Demanda por Bairro (Inviáveis)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topBairros.length > 0 ? (
                  <div className="space-y-2">
                    {topBairros.map(([bairro, count], index) => (
                      <div key={bairro} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? "bg-amber-500 text-white" : "bg-slate-700 text-slate-300"
                          }`}>
                            {index + 1}
                          </span>
                          <span className="text-sm text-slate-300 truncate max-w-[140px]">{bairro}</span>
                        </div>
                        <span className="text-sm font-bold text-red-400">{count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Nenhuma consulta inviável</p>
                )}
              </CardContent>
            </Card>
            
            <p className="text-xs text-slate-500 mt-4 text-center">
              Use o filtro para visualizar tipos específicos de consultas
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
